<!--
@component
Horizontal group of Buttons that share a size. Adjacent buttons share one 1px seam, and the hovered or focused button is raised so its focus ring isn't clipped. `size` on the group is the default for every child; a child can override it.

@use-instead-of flex wrappers that repeat size on every Button
@see Button
-->
<script module lang="ts">
  import type { ButtonSize } from "./Button.svelte";

  /** Lets a `ButtonGroup` supply the default `size` to its `Button` children. */
  export type ButtonGroupContext = { readonly size: ButtonSize };
  export const BUTTON_GROUP_CONTEXT = Symbol("button-group-context");
</script>

<script lang="ts">
  import type { Snippet } from "svelte";
  import { setContext } from "svelte";
  import type { SvelteHTMLElements } from "svelte/elements";

  type Props = SvelteHTMLElements["div"] & {
    size?: ButtonSize;
    children: Snippet;
  };

  let {
    children,
    size = "regular",
    class: classOverride,
    ...rest
  }: Props = $props();

  setContext<ButtonGroupContext>(BUTTON_GROUP_CONTEXT, {
    get size() {
      return size;
    },
  });
</script>

<div
  class={["btn-group", size === "small" && "btn-sm", classOverride]}
  role="group"
  {...rest}
>
  {@render children()}
</div>

<style lang="postcss">
  @reference "$app.css";

  .btn-group {
    @apply isolate inline-flex;
  }

  /* Line up with regular text buttons and enable stacking for hover/focus. */
  .btn-group :global(.btn) {
    @apply relative;
    @apply min-h-8.5;

    &.btn-sm {
      @apply min-h-5.5; /* Match the height of a small text button. */
    }
  }

  /* Every button after the first overlaps the shared border and squares its
     left edge. Keyed off the `.btn` class (not :first-child) so injected
     siblings like tooltip popovers don't shift which button counts as first. */
  .btn-group :global(.btn ~ .btn) {
    margin-left: -1px;
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }

  /* Every button that still has a button after it squares its right edge; the
     last button keeps its rounded right corners even when a tooltip div is
     inserted as its next sibling. */
  .btn-group :global(.btn:has(~ .btn)) {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }

  /* Raise the interacted button so its full border and focus ring render
     above its neighbors instead of being clipped by the overlap. */
  .btn-group :global(.btn:hover:not(:disabled)),
  .btn-group :global(.btn:active:not(:disabled)),
  .btn-group :global(.btn:focus-visible) {
    z-index: 1;
  }
</style>
